import React, { useState } from 'react';
import { Box, Chip, Collapse, Paper, Stack, Typography, IconButton, CircularProgress } from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export interface ToolCallItem {
  id?: string;
  name: string;
  arguments?: any;
  result?: any;
  isError?: boolean;
}

interface McpToolCallChipProps {
  toolCall: ToolCallItem;
  compact?: boolean;
}

export const McpToolCallChip: React.FC<McpToolCallChipProps> = ({ toolCall, compact }) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  const isError = toolCall.isError === true;
  const hasResult = toolCall.result !== undefined;

  if (compact) {
    let bg = '#f0f9ff'; // in-progress
    let icon = <CircularProgress size={10} />;

    if (hasResult) {
      if (isError) {
        bg = '#fff1f2';
        icon = <ErrorOutlineIcon color="error" sx={{ fontSize: 12 }} />;
      } else {
        bg = '#f0fdf4';
        icon = <CheckCircleOutlineIcon color="success" sx={{ fontSize: 12 }} />;
      }
    }

    return (
      <Chip
        icon={icon}
        label={toolCall.name}
        size="small"
        sx={{
          height: 20,
          fontSize: '0.65rem',
          fontWeight: 600,
          bgcolor: bg,
          my: 0.25,
          mr: 0.5,
        }}
      />
    );
  }

  return (
    <Box sx={{ my: 0.75, maxWidth: '100%' }}>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: '10px',
          bgcolor: isError ? '#fff1f2' : '#f8fafc',
          borderColor: isError ? '#fecdd3' : '#e2e8f0',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1.25, py: 0.75, cursor: 'pointer' }}
          onClick={() => setExpanded(!expanded)}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <BuildIcon sx={{ fontSize: 15, color: isError ? 'error.main' : 'primary.main' }} />
            <Typography variant="caption" fontWeight={700} sx={{ color: isError ? 'error.main' : 'text.primary' }}>
              {toolCall.name}
            </Typography>
            {hasResult && (
              <Chip
                size="small"
                label={isError ? 'Failed' : 'Executed'}
                color={isError ? 'error' : 'success'}
                icon={isError ? <ErrorOutlineIcon /> : <CheckCircleOutlineIcon />}
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            )}
          </Stack>

          <IconButton size="small" sx={{ p: 0.25 }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Stack>

        <Collapse in={expanded}>
          <Box sx={{ p: 1.25, pt: 0.5, borderTop: '1px solid', borderColor: isError ? '#fecdd3' : '#e2e8f0' }}>
            {toolCall.arguments && (
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.25 }}
                >
                  Arguments:
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1,
                    bgcolor: '#ffffff',
                    fontFamily: 'monospace',
                    fontSize: '0.72rem',
                    maxHeight: 120,
                    overflow: 'auto',
                  }}
                >
                  <pre style={{ margin: 0 }}>
                    {typeof toolCall.arguments === 'object'
                      ? JSON.stringify(toolCall.arguments, null, 2)
                      : String(toolCall.arguments)}
                  </pre>
                </Paper>
              </Box>
            )}

            {hasResult && (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color={isError ? 'error.main' : 'text.secondary'}
                  sx={{ display: 'block', mb: 0.25 }}
                >
                  {isError ? 'Error Result:' : 'Result:'}
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1,
                    bgcolor: isError ? '#fff1f2' : '#ffffff',
                    fontFamily: 'monospace',
                    fontSize: '0.72rem',
                    maxHeight: 160,
                    overflow: 'auto',
                  }}
                >
                  <pre style={{ margin: 0 }}>
                    {typeof toolCall.result === 'object'
                      ? JSON.stringify(toolCall.result, null, 2)
                      : String(toolCall.result)}
                  </pre>
                </Paper>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default McpToolCallChip;
